import { z } from 'zod';
import { Entity } from '@mainstage/catalog-model';

import { CompletePermission } from './model';

/**
 * Container header for all witboost user permissions
 */
export type MeshPermissionsHeader = {
  permissions: PermissionsMap;
};

/**
 * Map of permission IDs to their scopes, grouped by subject and role (i.e., assignment).
 *
 * Structure:
 * - Key: permission ID (e.g., "catalog.entity.read")
 * - Value: Array of scope groups, where each group contains scopes from the same subject-role assignment
 *
 * Example:
 * ```ts
 * {
 *   "catalog.entity.read": [
 *     ["urn:dmb:dmn:finance", "urn:dmb:dmn:hr"],        // from subject1 + role1
 *     ["urn:dmb:dmn:marketing", "urn:dmb:dmn:sales"]    // from subject2 + role2
 *   ]
 * }
 * ```
 */
export type PermissionsMap = {
  [permissionId: string]: PermissionScope[][];
};

export const PERMISSION_WILDCARD: string = '*';
export const PermissionScopeSchema = z.union([
  z.string().startsWith('urn:dmb:'),
  z.literal(PERMISSION_WILDCARD),
]);

export type PermissionScope = z.infer<typeof PermissionScopeSchema>;

export class UserPermissionsMap {
  constructor(private readonly map: PermissionsMap) {}

  static fromPermissions(
    permissions: CompletePermission[],
  ): UserPermissionsMap {
    // Use nested Maps for O(1) lookup: permissionId -> (subject_roleId -> scopes)
    const grouped = new Map<string, Map<string, string[]>>();

    permissions.forEach(permission => {
      const scopeGroups =
        grouped.get(permission.id) ?? new Map<string, string[]>();

      if (!grouped.has(permission.id)) grouped.set(permission.id, scopeGroups);

      // Use subject_roleId as key to group scopes belonging to the same assignment
      const groupKey = `${permission.subject}_${permission.roleId}`;
      const scopes = scopeGroups.get(groupKey) ?? [];

      if (permission.entityRef) scopes.push(permission.entityRef);

      if (!scopeGroups.has(groupKey) && scopes.length > 0)
        scopeGroups.set(groupKey, scopes);
    });

    // Convert to final format: PermissionsMap with array of arrays
    const permissionsMap: PermissionsMap = {};

    grouped.forEach((scopeGroups, permId) => {
      permissionsMap[permId] = Array.from(scopeGroups.values());
    });

    return new UserPermissionsMap(permissionsMap);
  }

  hasPermission(permissionId: string): boolean {
    return this.map[permissionId] ? true : false;
  }

  getScopes(permissionId: string): string[][] {
    return this.map[permissionId] ?? [];
  }

  getEntrySet(): PermissionsMap {
    return this.map;
  }
}

export interface RoleViewFilters {
  text?: string;
  limit?: number;
}

export interface PermissionViewFilters {
  text?: string;
  limit?: number;
}

export interface RoleAssignments {
  roleId: string;
  scopes: { urn: string; entity?: Entity; enabled?: boolean }[];
  userOrGroup: { urn: string; entity: Entity };
}
